interface RadarChartProps {
  bust: number;
  waist: number;
  hip: number;
  height: number;
}

const RANGES = [
  { label: "バスト", min: 70, max: 120, unit: "cm" },
  { label: "ウエスト", min: 50, max: 80, unit: "cm" },
  { label: "ヒップ", min: 70, max: 110, unit: "cm" },
  { label: "身長", min: 140, max: 185, unit: "cm" },
];

export function RadarChart({ bust, waist, hip, height }: RadarChartProps) {
  const cx = 160;
  const cy = 140;
  const maxR = 100;

  const normalize = (value: number, min: number, max: number) =>
    Math.min(1, Math.max(0, (value - min) / (max - min)));

  const rawValues = [bust, waist, hip, height];
  const values = rawValues.map((v, i) => normalize(v, RANGES[i]!.min, RANGES[i]!.max));

  const labels = RANGES.map((r) => r.label);
  const angles = values.map((_, i) => (Math.PI / 2) + (2 * Math.PI * i) / 4);

  const points = values.map((v, i) => ({
    x: cx + maxR * v * Math.cos(angles[i]!),
    y: cy - maxR * v * Math.sin(angles[i]!),
  }));

  const polygon = points.map((p) => `${p.x},${p.y}`).join(" ");

  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="flex flex-col gap-4">
      <svg viewBox="0 0 320 280" className="w-full max-w-[320px] mx-auto">
        {gridLevels.map((level) => {
          const gridPoints = angles
            .map((angle) => {
              const r = maxR * level;
              return `${cx + r * Math.cos(angle)},${cy - r * Math.sin(angle)}`;
            })
            .join(" ");
          return (
            <polygon
              key={level}
              points={gridPoints}
              fill="none"
              stroke="var(--border)"
              strokeWidth="1"
              opacity="0.5"
            />
          );
        })}

        {angles.map((angle, i) => (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={cx + maxR * Math.cos(angle)}
            y2={cy - maxR * Math.sin(angle)}
            stroke="var(--border)"
            strokeWidth="1"
            opacity="0.5"
          />
        ))}

        <polygon
          points={polygon}
          fill="var(--primary)"
          fillOpacity="0.2"
          stroke="var(--primary)"
          strokeWidth="2"
        />

        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill="var(--primary)" />
        ))}

        {angles.map((angle, i) => {
          const labelR = maxR + 24;
          const lx = cx + labelR * Math.cos(angle);
          const ly = cy - labelR * Math.sin(angle);
          return (
            <text
              key={i}
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="central"
              fill="var(--muted-foreground)"
              fontSize="12"
              fontFamily="var(--font-secondary)"
            >
              {labels[i]}
            </text>
          );
        })}
      </svg>

      {/* 根拠の説明 */}
      <div className="text-xs text-muted-foreground font-[var(--font-secondary)] space-y-1 px-2">
        <p className="font-medium text-foreground/70">各軸の基準範囲:</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
          {RANGES.map((r, i) => (
            <span key={r.label}>
              {r.label}: {r.min}〜{r.max}{r.unit}
              {rawValues[i]! > 0 && (
                <span className="text-primary ml-1">({rawValues[i]}{r.unit})</span>
              )}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground/70 pt-1">
          ※ 各数値を上記の範囲内で正規化(0〜100%)して表示しています
        </p>
      </div>
    </div>
  );
}
